import { ChevronLeft, ChevronRight, Download, ExternalLink } from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";

export interface LightboxItem {
  url: string;
  kind: "image" | "video";
  title?: string;
}

export interface MediaPreviewLightboxProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  items: LightboxItem[];
  index: number;
  onIndexChange: (i: number) => void;
  onDownload?: (item: LightboxItem) => void;
  onEnterDetail?: () => void;
  detailLabel?: string;
}

/**
 * 统一媒体预览 lightbox(泛化自 poster ImagePreviewLightbox):image|video 双形态,暗底 contain,
 * 左右切换,download/enterDetail 由调用方注入(解耦 posterService)。
 */
export function MediaPreviewLightbox({
  open,
  onOpenChange,
  items,
  index,
  onIndexChange,
  onDownload,
  onEnterDetail,
  detailLabel = "进入详情",
}: MediaPreviewLightboxProps) {
  const item = items[index];
  if (!item) return null;
  const many = items.length > 1;
  const prev = () => onIndexChange((index - 1 + items.length) % items.length);
  const next = () => onIndexChange((index + 1) % items.length);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* ★h-[85dvh] 定高(与基类 max-h 同值,不打架):媒体改绝对定位后不再撑高容器,
          高度链必须从根**定**下来 —— 否则 stage flex-1 在内容 0 时塌成 0(真机量到过)。
          lightbox 恒定大画幅本就是预期形态(小图居中留暗边)。 */}
      <DialogContent className="h-[85dvh] max-w-4xl gap-3 p-4">
        <DialogTitle className="sr-only">{item.title ?? "媒体预览"}</DialogTitle>
        {/* ★媒体区用 flex-1 + min-h-0,高度由弹窗(max-h-[85dvh])减去 footer 决定,媒体再 max-h-full
            收进来。原来媒体写死 max-h-[70vh]、容器 min-h-[40vh] —— 这两个 vh 值与 DialogContent 的
            85dvh 各自为政:媒体区不可压缩(缺 min-h-0),浏览器缩放(125%/150%,笔记本常见)时把弹窗
            max-h 顶破,弹窗比视口高、居中后顶底对称溢出,关闭按钮与底部「下载/进入详情」被切出屏幕。
            测试环境实测:1366×768@125% 长图预览顶底各裁 14px(2026-08-12 反馈)。 */}
        {/* ★媒体绝对定位钉进容器(0816 六人「看原图只见中段,上下滑不动」):
            上一版 img 直接 max-h-full,真机量出计算值 100% 却实渲染 1532px > 容器 703px ——
            百分比 max-height 在「三段式滚动壳里的 flex-1 项」上不解析(高度不定),
            图从弹窗两端溢出,items-center 让顶部那截永远滚不到。绝对定位的百分比按
            relative 祖先的**已定**高度解析,m-auto 居中;overflow-hidden 兜底:
            万一约束再失效也裁在框内,绝不再骑到弹窗外面。 */}
        <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-lg bg-black/40">
          {item.kind === "image" ? (
            <img
              src={item.url}
              alt={item.title ?? ""}
              className="absolute inset-0 m-auto max-h-full max-w-full object-contain"
            />
          ) : (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              src={item.url}
              controls
              autoPlay
              className="absolute inset-0 m-auto max-h-full max-w-full object-contain"
            />
          )}
          {many && (
            <>
              <button
                type="button"
                aria-label="上一个"
                onClick={prev}
                className="absolute left-2 flex h-9 w-9 items-center justify-center rounded-full bg-background/70 hover:bg-background"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                aria-label="下一个"
                onClick={next}
                className="absolute right-2 flex h-9 w-9 items-center justify-center rounded-full bg-background/70 hover:bg-background"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
        <div className="flex items-center justify-between">
          <span className="truncate text-sm text-muted-foreground">
            {item.title}
            {many ? ` · ${index + 1}/${items.length}` : ""}
          </span>
          <div className="flex gap-2">
            {onDownload && (
              <Button variant="outline" size="sm" onClick={() => onDownload(item)}>
                <Download className="mr-1 h-4 w-4" />
                下载
              </Button>
            )}
            {onEnterDetail && (
              <Button size="sm" onClick={onEnterDetail}>
                <ExternalLink className="mr-1 h-4 w-4" />
                {detailLabel}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
